import React from 'react';
import { Link } from 'react-router-dom';
import { Pickaxe, Building2, HeartPulse, Landmark, Factory, Fuel, PawPrint } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { INDUSTRIES, PARTNERS, FAQS } from '../mock';
import Reveal from './Reveal';

const ICONS = { Pickaxe, Building2, HeartPulse, Landmark, Factory, Fuel, PawPrint };
export const slugify = (s) => s.toLowerCase().replace(/\s+/g, '-');

function PartnerLogo({ name, domain, logo, dark, light, zoom }) {
  const [errored, setErrored] = React.useState(false);

  if (logo) {
    const contain = dark || light;
    return (
      <span className={`group inline-flex h-16 w-52 items-center justify-center overflow-hidden rounded-xl hover:opacity-90 transition-opacity shadow-lg ${dark ? 'bg-[#0f1c30] border border-white/10' : light ? 'bg-white' : ''}`}>
        <img
          src={logo}
          alt={name}
          style={zoom ? { transform: `scale(${zoom})` } : undefined}
          className={`transition-[filter] duration-500 ${contain ? 'h-full w-full object-contain p-2.5' : 'h-full w-full object-cover'}`}
        />
      </span>
    );
  }

  if (errored || !domain) {
    return (
      <span className="inline-flex h-16 w-52 items-center justify-center rounded-xl bg-white/5 border border-white/10 font-display text-lg font-bold text-white/50">
        {name}
      </span>
    );
  }
  return (
    <span className="inline-flex h-16 w-52 items-center justify-center gap-3 rounded-xl bg-white px-4 opacity-70 hover:opacity-100 transition-opacity shadow-lg">
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
        alt={name}
        onError={() => setErrored(true)}
        className="h-7 w-7 shrink-0 object-contain"
      />
      <span className="font-display text-lg font-bold text-[#0f1c30] tracking-tight">{name}</span>
    </span>
  );
}

export default function Industries() {
  return (
    <>
      {/* INDUSTRIES */}
      <section id="industries" className="relative py-24 px-5">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <span className="font-mono-accent text-xs text-[#0a6bd6]">// SECTORS</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Industries We Serve</h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ICONS[ind.icon];
              return (
                <Reveal key={ind.name} delay={i * 80}>
                  <Link to={`/industries#sector-${slugify(ind.name)}`} data-testid={`home-sector-${slugify(ind.name)}`} className="group relative block overflow-hidden neon-border rounded-3xl h-52 hover:-translate-y-2 transition-all duration-300">
                    <img src={ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-[#0a1526]/40 to-transparent" />
                    <span className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-xl backdrop-blur-md" style={{ backgroundColor: `${ind.color}26` }}>
                      <Icon className="h-5 w-5" style={{ color: ind.color }} />
                    </span>
                    <h3 className="absolute bottom-5 left-5 right-5 font-display text-xl font-bold text-white">{ind.name}</h3>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="relative py-24 px-5 grid-bg">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <span className="font-mono-accent text-xs text-[#0ef3fe]">// TECHNOLOGY PARTNERS</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Powered by the Best</h2>
              <p className="text-white/55 mt-4 max-w-xl mx-auto">We partner with the world's leading security and technology brands to deliver best-in-class solutions.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 justify-items-center">
            {PARTNERS.map((p, i) => (
              <PartnerLogo key={i} name={p.name} domain={p.domain} logo={p.logo} dark={p.dark} light={p.light} zoom={p.zoom} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-24 px-5 grid-bg">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <span className="font-mono-accent text-xs text-[#088ffe]">// FAQ</span>
              <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Frequently Asked Questions</h2>
            </div>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="neon-border rounded-2xl px-6 border-white/10">
                  <AccordionTrigger className="text-left font-display font-semibold hover:no-underline hover:text-[#0ef3fe] py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
